import * as ActionCable from '@rails/actioncable';
import { ROUTE_MAP } from '../../services/messaging-navigation';
import { getOwner } from '@ember/application';
import { isTest } from 'ember-simplepractice/utils/is-testing';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import ActiveConversationHandler from './activity-handlers/active-conversation';
import ConversationHandler from './activity-handlers/conversation';
import MessageHandler from './activity-handlers/message';
import Service, { service } from '@ember/service';
import emberConfig from 'ember-get-config';

if (emberConfig.environment === 'development' && !isTest()) {
  ActionCable.logger.enabled = true;
}

export default class WebsocketMessagingWebsocketService extends Service {
  @service messaging;
  @service messagingNavigation;
  @service store;

  @tracked isConnected = false;

  conversationHandler;
  activeConversationHandler;
  socket;
  subscription;
  contactSubscriptions = new Map();

  constructor() {
    super(...arguments);
    this.#initHandlers();
  }

  openSocket(messagingApiUrl, token) {
    const socketUrl = this.#buildSocketUrl(messagingApiUrl, token);
    this.socket = ActionCable.createConsumer(socketUrl);
    this.subscription = this.socket.subscriptions.create(
      { channel: 'ActivityChannel' },
      {
        connected: ({ reconnected }) => {
          this.handleConnection.perform(reconnected);
        },
        disconnected: () => {
          this.isConnected = false;
        },
        received: response => {
          this.handleActivity(response);
        },
      }
    );
  }

  connectSocket() {
    this.socket?.connect();
  }

  closeSocket() {
    this.socket?.disconnect();
  }

  handleConnection = task(async reconnected => {
    if (reconnected && !isTest()) {
      // Socket reconnection causes cleanup timing issues in tests.
      await this.reconnect.perform();
    }
    this.isConnected = true;
  });

  handleActivity(response) {
    switch (response.action) {
      case 'conversation':
        this.conversationHandler.handleActivity(response);
        break;
      case 'message':
        this.messageHandler.handleActivity(response);
        break;
      case 'contacts':
        this.handleContactsActivity();
        break;
      default:
        return false;
    }
  }

  handleActiveConversationUpdate(response) {
    this.activeConversationHandler.handleActivity(response);
  }

  handleContactsActivity() {
    // Consider kicking this out to a handler class if sufficiently complicated.
  }

  #initHandlers() {
    this.conversationHandler = new ConversationHandler(getOwner(this));
    this.activeConversationHandler = new ActiveConversationHandler(getOwner(this));
    this.messageHandler = new MessageHandler(getOwner(this));
  }

  reconnect = task({ drop: true }, async () => {
    await this.messaging.refreshProfileAndConversations.perform();

    if (this.messagingNavigation.isCurrentRoute(ROUTE_MAP.conversation)) {
      await this.messagingNavigation.currentRoute.handler.getMessages.perform();
    }
  });

  subscribeToActiveConversation(externalId, organizationId) {
    if (!this.socket || !this.isConnected) {
      return null;
    }

    const subscriptionKey = `${organizationId}:${externalId}`;

    if (this.contactSubscriptions.has(subscriptionKey)) {
      return this.contactSubscriptions.get(subscriptionKey);
    }

    const channelParams = {
      channel: 'ActiveConversationChannel',
      ['external_id']: externalId,
    };

    const subscription = this.socket.subscriptions.create(channelParams, {
      received: response => {
        this.handleActiveConversationUpdate(response);
      },
    });

    this.contactSubscriptions.set(subscriptionKey, subscription);
    return subscription;
  }

  unsubscribeFromActiveConversation(externalId, organizationId) {
    const subscriptionKey = `${organizationId}:${externalId}`;
    const subscription = this.contactSubscriptions.get(subscriptionKey);

    if (subscription) {
      subscription.unsubscribe();
      this.contactSubscriptions.delete(subscriptionKey);
    }
  }

  #buildSocketUrl(messagingApiUrl, token) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = new URL(messagingApiUrl).host;
    return `${protocol}//${host}/websocket?access_token=${token}`;
  }
}
